import { useState } from "react";
import { useTranslation } from "react-i18next";
import { AlertTriangle, Loader2, ShoppingBag, UserPlus } from "lucide-react";
import { legalIdentityComplete, salesOpen, type SiteLegal, type SitePricing, type SiteSignup } from "@wedding/shared";
import { adminSiteApi } from "../lib/api";
import { AppLink } from "../lib/route-primitives";
import { paths } from "../lib/paths";
import { OrdersPanel } from "./site-orders";
import { AddonsPanel } from "./site-addons";

/**
 * "Satış & Site → Online satış" (F8b): online satış anahtarı (`signup.orders`)
 * + sipariş listesi. Organizatörün kendi hesabını açabilmesi (`signup.selfServe`)
 * platform politikası olduğu için Kontrol Merkezi'nde (2026-10-14). Satış ancak
 * fiyatlar görünür ve yasal kimlik tamken landing'de görünür (`salesOpen`).
 */
export function SalesTab({ signup, legal, pricing, onSignupChange, showToast }: {
  signup: SiteSignup;
  legal: SiteLegal;
  pricing: SitePricing;
  onSignupChange: (s: SiteSignup) => void;
  showToast: (msg: string, ok?: boolean) => void;
}) {
  const { t } = useTranslation();
  const [saving, setSaving] = useState<"orders" | null>(null);

  const save = async (next: SiteSignup, field: "orders") => {
    setSaving(field);
    try {
      const res = await adminSiteApi.put("signup", next);
      onSignupChange(res.data ?? next);
      showToast(t("siteAdmin.saved"));
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      // Satış, kayıt defteri (migration 057) olmadan açılamaz — sözleşme kanıtı tutulmalı.
      if (msg === "ledger_unavailable") showToast(t("siteAdmin.sales.ledgerRequired"), false);
      else showToast(t("siteAdmin.saveFailed", { error: msg }), false);
    } finally {
      setSaving(null);
    }
  };

  const toggleOrders = async (orders: boolean) => {
    if (orders && !window.confirm(t("siteAdmin.sales.ordersOpenConfirm"))) return;
    await save({ ...signup, orders }, "orders");
  };

  const legalOk = legalIdentityComplete(legal);
  const live = salesOpen({ signup, legal, pricing });

  return (
    <div className="space-y-6">
      <p className="flex items-center gap-2 text-xs text-muted-foreground" data-testid="selfserve-moved">
        <UserPlus className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
        <span>
          {t("siteAdmin.sales.selfServeMoved")}{" "}
          <AppLink to={paths.controlCenter} className="underline underline-offset-2 cursor-pointer">{t("nav.controlCenter")}</AppLink>
        </span>
      </p>

      <div className="rounded-2xl border border-border bg-card px-5 py-4 space-y-3">
        <div className="flex items-center gap-4">
          <ShoppingBag className="h-5 w-5 text-primary shrink-0" aria-hidden="true" />
          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium text-foreground">{t("siteAdmin.sales.ordersTitle")}</p>
            <p className="text-xs text-muted-foreground mt-0.5" data-testid="orders-state">
              {live ? t("siteAdmin.sales.ordersLive") : signup.orders ? t("siteAdmin.sales.ordersBlocked") : t("siteAdmin.sales.ordersOff")}
            </p>
          </div>
          <label className="inline-flex items-center gap-2 text-sm cursor-pointer select-none">
            {saving === "orders" && <Loader2 className="h-4 w-4 animate-spin" />}
            <input
              type="checkbox"
              checked={signup.orders}
              disabled={saving !== null || (!signup.orders && !legalOk)}
              onChange={(e) => void toggleOrders(e.target.checked)}
              aria-label={t("siteAdmin.sales.ordersTitle")}
              className="h-4 w-4 accent-primary cursor-pointer disabled:cursor-not-allowed"
            />
            {signup.orders ? t("controlCenter.enabled") : t("controlCenter.disabled")}
          </label>
        </div>
        {(!legalOk || !pricing.show) && (
          <ul className="space-y-1.5" data-testid="orders-blockers">
            {!legalOk && (
              <li className="flex items-start gap-2 text-xs text-amber-700 dark:text-amber-400">
                <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-0.5" aria-hidden="true" />
                <span>
                  {t("siteAdmin.sales.needLegal")}{" "}
                  <AppLink to={paths.siteTab("legal")} className="underline underline-offset-2 cursor-pointer">{t("siteAdmin.tabs.legal")}</AppLink>
                </span>
              </li>
            )}
            {!pricing.show && (
              <li className="flex items-start gap-2 text-xs text-amber-700 dark:text-amber-400">
                <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-0.5" aria-hidden="true" />
                <span>
                  {t("siteAdmin.sales.needPricing")}{" "}
                  <AppLink to={paths.siteTab("pricing")} className="underline underline-offset-2 cursor-pointer">{t("siteAdmin.tabs.pricing")}</AppLink>
                </span>
              </li>
            )}
          </ul>
        )}
        <p className="text-xs text-muted-foreground">{t("siteAdmin.sales.ordersHint")}</p>
      </div>

      <OrdersPanel bounds={pricing.bounds} showToast={showToast} />
      <AddonsPanel showToast={showToast} />
    </div>
  );
}
